import { createFileRoute, Link } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";

import { providerDashboard } from "@/lib/provider-payments.functions";
import { money } from "@/lib/provider-schemas";
import { downloadCsv } from "@/lib/csv-export";
import { ErrorBox, ProviderModuleShell, StatCard } from "@/components/admin/provider/ProviderModuleShell";
import { Button } from "@/components/ui/button";

const dashQuery = queryOptions({
  queryKey: ["admin", "provider-dashboard"],
  queryFn: () => providerDashboard(),
});

export const Route = createFileRoute("/_authenticated/admin/provider-payables")({
  head: () => ({
    meta: [
      { title: "Cuentas por pagar — Administración" },
      { name: "description", content: "Saldos por proveedor, pagos vencidos y próximos vencimientos." },
      { property: "og:title", content: "Cuentas por pagar — Administración · Mostrando México" },
      { property: "og:description", content: "Administración privada de proveedores por empresa." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  errorComponent: ({ error }) => <ErrorBox error={error as Error} />,
  notFoundComponent: () => <ErrorBox error={new Error("No encontrado")} />,
  component: () => (
    <ProviderModuleShell
      title="Cuentas por pagar"
      description="Cuánto se le debe a cada proveedor, qué pagos ya vencieron y qué vence en los próximos 7 días."
    >
      <Body />
    </ProviderModuleShell>
  ),
});

function Body() {
  const { data } = useSuspenseQuery(dashQuery);
  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Reservas activas" value={data.totals.activeBookings} hint={`${data.totals.pax} pax`} />
        <StatCard label="Pagos vencidos" value={data.overdue.length} />
        <StatCard label="Vencen en 7 días" value={data.upcoming.length} />
        <StatCard
          label="Total pendiente"
          value={
            <span className="text-base">
              {data.totals.byCurrency.map((c) => (
                <div key={c.currency}>{money(c.pending, c.currency)}</div>
              ))}
            </span>
          }
        />
      </div>

      <section className="rounded-xl border bg-card">
        <header className="flex items-center justify-between border-b px-4 py-3">
          <h2 className="font-semibold">Saldo por proveedor</h2>
          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              downloadCsv(
                "cuentas-por-pagar",
                [
                  { key: "name", label: "Proveedor" },
                  { key: "pending", label: "Pendiente" },
                ],
                data.topPending as never,
              )
            }
          >
            Exportar CSV
          </Button>
        </header>
        <table className="w-full text-sm">
          <tbody>
            {data.topPending.map((p) => (
              <tr key={p.id} className="border-b last:border-0">
                <td className="px-4 py-2 font-medium">{p.name}</td>
                <td className="px-4 py-2 text-right text-xs tabular-nums">
                  {p.currencies.map((c: any) => (
                    <div key={c.currency}>
                      {money(c.pending, c.currency)}{" "}
                      <span className="text-muted-foreground">
                        (costo {money(c.cost, c.currency)} · pagado {money(c.paid, c.currency)})
                      </span>
                    </div>
                  ))}
                </td>
                <td className="px-4 py-2 text-right">
                  <Link
                    to="/admin/provider-payments"
                    className="text-xs font-medium text-primary hover:underline"
                  >
                    Registrar pago
                  </Link>
                </td>
              </tr>
            ))}
            {!data.topPending.length ? (
              <tr>
                <td className="px-4 py-8 text-center text-muted-foreground">
                  No hay saldos pendientes.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </section>

      <BookingTable title="Pagos vencidos" rows={data.overdue} tone="destructive" />
      <BookingTable title="Próximos vencimientos (7 días)" rows={data.upcoming} />
    </div>
  );
}

function BookingTable({
  title,
  rows,
  tone,
}: {
  title: string;
  rows: Array<Record<string, any>>;
  tone?: "destructive";
}) {
  return (
    <section className="overflow-x-auto rounded-xl border bg-card">
      <header className="border-b px-4 py-3">
        <h2 className={`font-semibold ${tone === "destructive" ? "text-destructive" : ""}`}>
          {title} ({rows.length})
        </h2>
      </header>
      <table className="w-full text-sm">
        <thead className="bg-muted/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="px-3 py-2">Vence</th>
            <th className="px-3 py-2">Servicio</th>
            <th className="px-3 py-2">Tour</th>
            <th className="px-3 py-2">Cliente</th>
            <th className="px-3 py-2 text-right">Costo</th>
            <th className="px-3 py-2 text-right">Pagado</th>
            <th className="px-3 py-2 text-right">Pendiente</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t">
              <td className="px-3 py-2 whitespace-nowrap">{r.payment_due_date}</td>
              <td className="px-3 py-2 whitespace-nowrap">{r.service_date}</td>
              <td className="px-3 py-2 text-xs">{r.tour_title}</td>
              <td className="px-3 py-2 text-xs">
                {r.customer_first_name} {r.customer_last_name}
              </td>
              <td className="px-3 py-2 text-right tabular-nums">{money(r.provider_cost, r.cost_currency)}</td>
              <td className="px-3 py-2 text-right tabular-nums">{money(r.paid_amount, r.cost_currency)}</td>
              <td className="px-3 py-2 text-right font-semibold tabular-nums">
                {money(r.pending_amount, r.cost_currency)}
              </td>
            </tr>
          ))}
          {!rows.length ? (
            <tr>
              <td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">
                Nada por aquí.
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </section>
  );
}
